/**
 * Diligence API
 * =============
 * The two operator routes for a lead's company diligence run.
 *
 * A diligence run is a separate pipeline from lead research. It does not
 * increment `research_version`, and Refresh Research does not affect it.
 */

import apiClient from './client';

const LEADS_BASE = '/api/v1/leads';

/**
 * Start a diligence run for a lead
 *
 * Every field is optional. A person in a hurry posts an empty body.
 * `company_name`, `max_cost_usd` and `warn_cost_usd` are deliberately absent:
 * the first is the lead's company, and the two cost fields come from the
 * server environment.
 *
 * The call blocks while the service resolves the profile and runs its identity
 * gate, which takes seconds. The default 30-second client timeout is raised to
 * 70, one step above the backend's own 60-second service timeout, so a slow
 * gate reports its real answer instead of a client-side abort.
 *
 * @param {string} leadId - Lead UUID
 * @param {Object} payload - Run inputs, all optional
 * @param {string} [payload.website] - Only needed when the lead has none
 * @param {string} [payload.profile] - 'standard' or 'lite'
 * @returns {Promise<Object>} The whole row the start wrote, shaped exactly
 *   like an entry of the status route's `versions`, plus `estimated_minutes`
 *   and `message`. The card replaces its held row with it, so it carries
 *   `logs` — the step record, all three steps pending — and the card shows
 *   the run steps section without waiting for the first poll.
 * @throws {Error} 409 in flight, 422 validation, 503 switched off
 */
export const startDiligence = async (leadId, payload = {}) => {
  const response = await apiClient.post(
    `${LEADS_BASE}/${leadId}/diligence`,
    payload,
    { timeout: 70000 },
  );
  return response.data;
};

/**
 * Read a lead's diligence run history
 *
 * Returns `{ run, versions, diligence_enabled }`. `run` is the lead's highest
 * version, and it is null for a lead that never had a run. `versions` holds
 * every version, newest first, each shaped exactly like `run`. It is empty for
 * that same lead.
 *
 * `run` is kept as its own field rather than read as `versions[0]`. Three
 * readers use it, and none of them should have to know about the indexing.
 *
 * This route also applies the one-hour timeout rule, so a silent run becomes
 * `timed_out` on the next read.
 *
 * Every `pdf_url` — on `run` and on each entry of `versions` — is signed at
 * read time and expires one hour after that read. Never store one and never
 * reuse one from an earlier poll.
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} `{ data: { run, versions, diligence_enabled } }`
 */
export const getDiligenceStatus = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/diligence`);
  return response.data;
};

const diligenceAPI = {
  startDiligence,
  getDiligenceStatus,
};

export default diligenceAPI;
